"use client";

import React, { useState } from "react";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Label } from "@/app/components/ui/label";
import { Checkbox } from "@/app/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Badge } from "@/app/components/ui/badge";
import { Loader2, Settings, Download, Globe, Image, Code, Palette, Type, Info } from "lucide-react";
import { toast } from "react-hot-toast";
import { trpc } from "@/lib/trpc";

interface CloneOptions {
  includeImages: boolean;
  includeScripts: boolean;
  includeStyles: boolean;
  includeFonts: boolean;
  includeVideos: boolean;
  includeAudio: boolean;
  includeDocuments: boolean;
  includeIcons: boolean;
  // Limits & crawling
  maxDepth: number;
  maxPages: number;
  maxFileSize: number;
  maxResources: number;
  timeout: number;
  concurrency: number;
  retries: number;
  // Network/scope
  userAgent: string;
  followRedirects: boolean;
  includeSubdomains: boolean;
  includeExternal: boolean;
  rewriteLinks: boolean;
  // Filtering
  includePatterns: string[];
  excludePatterns: string[];
}

export default function SiteCopier() {
  const [pageUrl, setPageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [options, setOptions] = useState<CloneOptions>({
    includeImages: true,
    includeScripts: true,
    includeStyles: true,
    includeFonts: true,
    includeVideos: false,
    includeAudio: false,
    includeDocuments: false,
    includeIcons: true,
    // Limits & crawling
    maxDepth: 1,
    maxPages: 50,
    maxFileSize: 10 * 1024 * 1024, // 10MB per resource
    maxResources: 500,
    timeout: 30000, // 30 seconds
    concurrency: 6,
    retries: 2,
    // Network/scope
    userAgent: "Mozilla/5.0 (compatible; SiteCopier/1.1)",
    followRedirects: true,
    includeSubdomains: false,
    includeExternal: false,
    rewriteLinks: true,
    // Filtering
    includePatterns: [],
    excludePatterns: [],
  });

  const featuresQuery = trpc.clone.getFeatures.useQuery();
  const cloneMutation = trpc.clone.clone.useMutation();

  const handleCopy = async () => {
    if (!pageUrl.trim()) {
      setError("Enter a valid URL");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await cloneMutation.mutateAsync({
        url: pageUrl.trim(),
        options: {
          includeImages: options.includeImages,
          includeScripts: options.includeScripts,
          includeStyles: options.includeStyles,
          includeFonts: options.includeFonts,
          includeVideos: options.includeVideos,
          includeAudio: options.includeAudio,
          includeDocuments: options.includeDocuments,
          includeIcons: options.includeIcons,
          // crawl/limits
          maxDepth: options.maxDepth,
          maxPages: options.maxPages,
          maxFileSize: options.maxFileSize,
          maxResources: options.maxResources,
          timeout: options.timeout,
          concurrency: options.concurrency,
          retries: options.retries,
          // scope/network
          userAgent: options.userAgent,
          followRedirects: options.followRedirects,
          includeSubdomains: options.includeSubdomains,
          includeExternal: options.includeExternal,
          rewriteLinks: options.rewriteLinks,
          // filters
          includePatterns: options.includePatterns,
          excludePatterns: options.excludePatterns,
        }
      });
      
      // Convert base64 to blob and download
      const binaryString = atob(result.contentBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/zip' });
      
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
      toast.success("Download started!");
      setPageUrl("");
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to clone site");
      toast.error(e.message || "Failed to clone site");
    } finally {
      setLoading(false);
    }
  };

  const updateOption = (key: keyof CloneOptions, value: any) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="basic">Basic</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Website URL
              </CardTitle>
              <CardDescription>
                Enter the URL of the website you want to clone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="site-url">Website URL</Label>
                <Input
                  id="site-url"
                  type="url"
                  placeholder="https://example.com"
                  value={pageUrl}
                  onChange={(e) => {
                    setPageUrl(e.target.value);
                    setError(null);
                  }}
                  disabled={loading}
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-images"
                    checked={options.includeImages}
                    onCheckedChange={(checked) => updateOption('includeImages', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-images" className="flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Images
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-scripts"
                    checked={options.includeScripts}
                    onCheckedChange={(checked) => updateOption('includeScripts', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-scripts" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Scripts
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-styles"
                    checked={options.includeStyles}
                    onCheckedChange={(checked) => updateOption('includeStyles', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-styles" className="flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Styles
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-fonts"
                    checked={options.includeFonts}
                    onCheckedChange={(checked) => updateOption('includeFonts', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-fonts" className="flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Fonts
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-videos"
                    checked={options.includeVideos}
                    onCheckedChange={(checked) => updateOption('includeVideos', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-videos" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Videos
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-audio"
                    checked={options.includeAudio}
                    onCheckedChange={(checked) => updateOption('includeAudio', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-audio" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Audio
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-docs"
                    checked={options.includeDocuments}
                    onCheckedChange={(checked) => updateOption('includeDocuments', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-docs" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Documents
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-icons"
                    checked={options.includeIcons}
                    onCheckedChange={(checked) => updateOption('includeIcons', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-icons" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Icons
                  </Label>
                </div>
              </div>

              <Button
                onClick={handleCopy}
                disabled={loading || !pageUrl.trim()}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cloning Website...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Clone Website
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Advanced Options
              </CardTitle>
              <CardDescription>
                Configure advanced cloning options for better control
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max-file-size">Max File Size (MB)</Label>
                  <Input
                    id="max-file-size"
                    type="number"
                    value={Math.round(options.maxFileSize / (1024 * 1024))}
                    onChange={(e) => updateOption('maxFileSize', parseInt(e.target.value) * 1024 * 1024)}
                    disabled={loading}
                    min="1"
                    max="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-resources">Max Resources</Label>
                  <Input
                    id="max-resources"
                    type="number"
                    value={options.maxResources}
                    onChange={(e) => updateOption('maxResources', parseInt(e.target.value))}
                    disabled={loading}
                    min="10"
                    max="2000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeout">Timeout (seconds)</Label>
                  <Input
                    id="timeout"
                    type="number"
                    value={Math.round(options.timeout / 1000)}
                    onChange={(e) => updateOption('timeout', parseInt(e.target.value) * 1000)}
                    disabled={loading}
                    min="5"
                    max="120"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-depth">Max Depth</Label>
                  <Input
                    id="max-depth"
                    type="number"
                    value={options.maxDepth}
                    onChange={(e) => updateOption('maxDepth', parseInt(e.target.value))}
                    disabled={loading}
                    min="0"
                    max="10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-pages">Max Pages</Label>
                  <Input
                    id="max-pages"
                    type="number"
                    value={options.maxPages}
                    onChange={(e) => updateOption('maxPages', parseInt(e.target.value))}
                    disabled={loading}
                    min="1"
                    max="1000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="concurrency">Concurrency</Label>
                  <Input
                    id="concurrency"
                    type="number"
                    value={options.concurrency}
                    onChange={(e) => updateOption('concurrency', parseInt(e.target.value))}
                    disabled={loading}
                    min="1"
                    max="20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retries">Retries</Label>
                  <Input
                    id="retries"
                    type="number"
                    value={options.retries}
                    onChange={(e) => updateOption('retries', parseInt(e.target.value))}
                    disabled={loading}
                    min="0"
                    max="5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-agent">User Agent</Label>
                  <Input
                    id="user-agent"
                    value={options.userAgent}
                    onChange={(e) => updateOption('userAgent', e.target.value)}
                    disabled={loading}
                    placeholder="Custom user agent"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="follow-redirects"
                  checked={options.followRedirects}
                  onCheckedChange={(checked) => updateOption('followRedirects', checked)}
                  disabled={loading}
                />
                <Label htmlFor="follow-redirects">Follow Redirects</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-subdomains"
                    checked={options.includeSubdomains}
                    onCheckedChange={(checked) => updateOption('includeSubdomains', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-subdomains">Include Subdomains</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-external"
                    checked={options.includeExternal}
                    onCheckedChange={(checked) => updateOption('includeExternal', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="include-external">Include External Links</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rewrite-links"
                    checked={options.rewriteLinks}
                    onCheckedChange={(checked) => updateOption('rewriteLinks', checked)}
                    disabled={loading}
                  />
                  <Label htmlFor="rewrite-links">Rewrite Links for Offline</Label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="include-patterns">Include URL Patterns (comma or newline separated)</Label>
                  <Input
                    id="include-patterns"
                    placeholder="e.g. /blog, /docs"
                    value={options.includePatterns.join(", ")}
                    onChange={(e) => updateOption('includePatterns', e.target.value.split(/,|\n/).map(s => s.trim()).filter(Boolean))}
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="exclude-patterns">Exclude URL Patterns (comma or newline separated)</Label>
                  <Input
                    id="exclude-patterns"
                    placeholder="e.g. /admin, /private"
                    value={options.excludePatterns.join(", ")}
                    onChange={(e) => updateOption('excludePatterns', e.target.value.split(/,|\n/).map(s => s.trim()).filter(Boolean))}
                    disabled={loading}
                  />
                </div>
              </div>

              <Button
                onClick={handleCopy}
                disabled={loading || !pageUrl.trim()}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cloning Website...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Clone Website
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {featuresQuery.data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Features & Limits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Supported Features:</h4>
                <div className="flex flex-wrap gap-2">
                  {featuresQuery.data.features.map((feature, index) => (
                    <Badge key={index} variant="secondary">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Current Limits:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <strong>Max File Size:</strong> {featuresQuery.data.limits.maxFileSize}
                  </div>
                  <div>
                    <strong>Max Resources:</strong> {featuresQuery.data.limits.maxResources}
                  </div>
                  <div>
                    <strong>Timeout:</strong> {featuresQuery.data.limits.timeout}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
